import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import * as React from "react";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { FrontPagePanelSize } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { IReportItem } from "source/models/report/reportItem";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { FilterSearchField } from "source/components/framework/Filters/FilterSearchField";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ISaveReportFilters } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";
import { ReportFilterPanelExtraParameters } from "source/components/framework/Report/ReportFilterPanelExtraParameters";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { FilterButtons } from "source/components/framework/Filters/FilterButtons";
import { PageChartFilterDrawer } from "source/components/framework/Page/PageChartFilterDrawer";

export interface IWhistleBlowReportFilterPanelProp {
    classes?: string;
    chartPanelDrawerMode?: boolean;
    report: IReportItem;
    units: IIdTitleNode[];
    charts?: IIdTitle[];
    categories: IIdTitle[];
    handlers: IIdTitle[];
    stateFilters: IIdTitle[];
    severityFilters: IIdTitle[];
    resolutionFilters: IIdTitle[];
    sourceFilters: IIdTitle[];
    anonymityFilters: IIdTitle[];
    dynamicTimeFilters: IIdTitle[];
    panelsDynamicTimeFilters: IIdTitle[];
    panelSize?: number;
    panelVisible?: boolean;
    selectedChartId?: string;
    selectedStartDate: number;
    selectedEndDate: number;
    selectedUnitIds: string[];
    selectedCategoryIds: string[];
    selectedHandlerUserCodes: string[];
    selectedStateFilterId: string;
    selectedSeverityFilterId: string;
    selectedResolutionFilterId: string;
    selectedSourceFilterId: string;
    selectedAnonymityFilterId: string;
    selectedDynamicTimeFilterId: string;
    canRemoveSharedReports: boolean;
    onSaveReport: (saveAs: boolean, onGetReportSaveFilters: () => ISaveReportFilters) => void;
    onRemoveReport: (filterId: string) => void;
    onOk: (closeDialog: boolean, startDate: number, endDate: number, selectedChartId: string, selectedStateFilterId: string, selectedSeverityFilterId: string, selectedResolutionFilterId: string,
        selectedSourceFilterId: string, selectedAnonymityFilterId: string, selectedDynamicTimeFilterId: string, selectedCategoryIds: string[], selectedHandlerUserCodes: string[], selectedUnitIds: string[],
        parameters: ITaskDataRow) => ISaveReportFilters;
    onOkChart?: (startDate: number, endDate: number, selectedChartId: string, selectedStateFilterId: string, selectedSeverityFilterId: string, selectedResolutionFilterId: string,
        selectedSourceFilterId: string, selectedAnonymityFilterId: string, selectedDynamicTimeFilterId: string, selectedCategoryIds: string[], selectedHandlerUserCodes: string[], selectedUnitIds: string[],
        parameters: ITaskDataRow,
        panelSize: number, panelVisible: boolean) => void;
    onCancel: () => void;
}

export interface IWhistleBlowReportFilterPanelState {
    panelSize: string;
    panelVisible: boolean;
    startDateStr: string;
    endDateStr: string;
    selectedChartId: string;
    selectedStateFilterId: string;
    selectedSeverityFilterId: string;
    selectedResolutionFilterId: string;
    selectedSourceFilterId: string;
    selectedAnonymityFilterId: string;
    selectedDynamicTimeFilterId: string;
    selectedCategoryIds: string[];
    selectedHandlerUserCodes: string[];
    selectedUnitIds: string[];
    extraParameters: ITaskDataRow;
    startEndDateDisabled: boolean;
}

export class WhistleBlowReportFilterPanel extends React.Component<IWhistleBlowReportFilterPanelProp, IWhistleBlowReportFilterPanelState> {
    private orgStateHash: string = "";

    getStateFromProps = (props: IWhistleBlowReportFilterPanelProp): IWhistleBlowReportFilterPanelState => {
        return {
            panelSize: ((props.chartPanelDrawerMode ? props.panelSize : null) ?? FrontPagePanelSize.OneXOne).toString(10),
            panelVisible: (props.chartPanelDrawerMode ? props.panelVisible : null) ?? false,
            startDateStr: props.selectedStartDate ? Base.timeToDateStr(props.selectedStartDate) : "",
            endDateStr: props.selectedEndDate ? Base.timeToDateStr(props.selectedEndDate) : "",
            selectedChartId: props.chartPanelDrawerMode ? props.selectedChartId : "",
            selectedStateFilterId: props.selectedStateFilterId,
            selectedSeverityFilterId: props.selectedSeverityFilterId,
            selectedResolutionFilterId: props.selectedResolutionFilterId,
            selectedSourceFilterId: props.selectedSourceFilterId,
            selectedAnonymityFilterId: props.selectedAnonymityFilterId,
            selectedDynamicTimeFilterId: props.selectedDynamicTimeFilterId,
            selectedCategoryIds: props.selectedCategoryIds.slice(0),
            selectedHandlerUserCodes: props.selectedHandlerUserCodes.slice(0),
            selectedUnitIds: props.selectedUnitIds.slice(0),
            extraParameters: props.report.taskType && props.report.taskDataRow ? new TaskDataRow(props.report.taskDataRow) : null,
            startEndDateDisabled: props.selectedDynamicTimeFilterId !== props.dynamicTimeFilters[0].id
        };
    };

    constructor(props: IWhistleBlowReportFilterPanelProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getSaveDataFromState(props, this.state).hash;
    }

    componentDidUpdate(prevProps: IWhistleBlowReportFilterPanelProp, prevState: IWhistleBlowReportFilterPanelState): void {
        const props = this.props;
        if (!prevProps || prevProps.report.getHash() === props.report.getHash()) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(props, state).hash;
    }

    handleTogglePanelVisible = () => {
        this.setState({ panelVisible: !this.state.panelVisible });
    };

    handleChangeUnitIds = (selectedUnitIds: string[]) => {
        this.setState({
            selectedUnitIds
        });
    };

    handleChangeCategoryIds = (selectedCategoryIds: string[]) => {
        this.setState({
            selectedCategoryIds
        });
    };

    handleChangeHandlerUserCodes = (selectedHandlerUserCodes: string[]) => {
        this.setState({
            selectedHandlerUserCodes
        });
    };

    handleStartDateChange = (value: string) => {
        this.setState({ startDateStr: value });
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDateStr: value });
    };

    handleSelectedDynamicTimeFilterIdChange = (value: string) => {
        if (value.toInteger() > 0) {
            this.setState({ selectedDynamicTimeFilterId: value, startDateStr: "", endDateStr: "", startEndDateDisabled: true });
        } else {
            this.setState({ selectedDynamicTimeFilterId: value, startEndDateDisabled: false });
        }
    };

    getSaveDataFromState = (props: IWhistleBlowReportFilterPanelProp, state: IWhistleBlowReportFilterPanelState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("panelSize", state.panelSize);
        result.append("panelVisible", state.panelVisible);
        result.append("startDate", state.startDateStr);
        result.append("endDate", state.endDateStr);
        result.append("selectedChartId", state.selectedChartId ? state.selectedChartId : null);
        result.append("selectedStateFilterId", state.selectedStateFilterId ? state.selectedStateFilterId : null);
        result.append("selectedSeverityFilterId", state.selectedSeverityFilterId ? state.selectedSeverityFilterId : null);
        result.append("selectedResolutionFilterId", state.selectedResolutionFilterId ? state.selectedResolutionFilterId : null);
        result.append("selectedSourceFilterId", state.selectedSourceFilterId ? state.selectedSourceFilterId : null);
        result.append("selectedAnonymityFilterId", state.selectedAnonymityFilterId ? state.selectedAnonymityFilterId : null);
        result.append("selectedDynamicTimeFilterId", state.selectedDynamicTimeFilterId ? state.selectedDynamicTimeFilterId : null);
        result.append("selectedCategoryIds", state.selectedCategoryIds);
        result.append("selectedHandlerUserCodes", state.selectedHandlerUserCodes);
        result.append("selectedUnitIds", state.selectedUnitIds);
        if (props.report.taskType && props.report.taskDataRow) {
            result.append("parameters", state.extraParameters.cols.map(i => state.extraParameters.getTaskDataRowColUiValue(i.field, props.report.taskType)));
        }
        return result;
    };

    handleSaveReport = async (saveAs: boolean) => {
        const props = this.props;
        const state = this.state;
        const startDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const endDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        if (!saveAs) {
            props.onOk(false, startDate, endDate, state.selectedChartId, state.selectedStateFilterId, state.selectedSeverityFilterId,
                state.selectedResolutionFilterId, state.selectedSourceFilterId, state.selectedAnonymityFilterId, state.selectedDynamicTimeFilterId,
                state.selectedCategoryIds, state.selectedHandlerUserCodes, state.selectedUnitIds, state.extraParameters);
            await this.props.onSaveReport(false, null);
        } else {
            await this.props.onSaveReport(true, () => {
                return props.onOk(false, startDate, endDate, state.selectedChartId, state.selectedStateFilterId, state.selectedSeverityFilterId,
                    state.selectedResolutionFilterId, state.selectedSourceFilterId, state.selectedAnonymityFilterId, state.selectedDynamicTimeFilterId,
                    state.selectedCategoryIds, state.selectedHandlerUserCodes, state.selectedUnitIds, this.state.extraParameters);
            });
        }
    };

    handleClear = () => {
        const props = this.props;
        const state = this.state;
        const extraParameters = state.extraParameters ? new TaskDataRow(state.extraParameters) : null;
        if (extraParameters) {
            extraParameters.initializeToTaskType(props.report.taskType);
        }
        this.setState({
            startDateStr: "",
            endDateStr: "",
            selectedChartId: props.chartPanelDrawerMode ? props.charts[0].id : "",
            selectedStateFilterId: props.stateFilters[1].id,
            selectedSeverityFilterId: props.severityFilters[0].id,
            selectedResolutionFilterId: props.resolutionFilters[0].id,
            selectedSourceFilterId: props.sourceFilters[0].id,
            selectedAnonymityFilterId: props.anonymityFilters[0].id,
            selectedCategoryIds: [props.categories[0].id],
            selectedHandlerUserCodes: [props.handlers[0].id],
            selectedUnitIds: [props.units[0].id],
            extraParameters
        });
    };

    handleSave = async () => {
        const props = this.props;
        const state = this.state;
        const startDate = state.startDateStr ? state.startDateStr.toDate().getTime() : null;
        const endDate = state.endDateStr ? state.endDateStr.toDate().getTime() : null;
        if (props.onOk) {
            props.onOk(true, startDate, endDate, state.selectedChartId, state.selectedStateFilterId, state.selectedSeverityFilterId,
                state.selectedResolutionFilterId, state.selectedSourceFilterId, state.selectedAnonymityFilterId, state.selectedDynamicTimeFilterId,
                state.selectedCategoryIds, state.selectedHandlerUserCodes, state.selectedUnitIds, state.extraParameters);
        } else if (props.onOkChart) {
            props.onOkChart(startDate, endDate, state.selectedChartId, state.selectedStateFilterId, state.selectedSeverityFilterId,
                state.selectedResolutionFilterId, state.selectedSourceFilterId, state.selectedAnonymityFilterId, state.selectedDynamicTimeFilterId,
                state.selectedCategoryIds, state.selectedHandlerUserCodes, state.selectedUnitIds, state.extraParameters, state.panelSize.toInteger(), state.panelVisible);
        }
    };

    handleCancel = async (checkChanges: boolean) => {
        AppUtils.cancelEditDialog(checkChanges, this.getSaveDataFromState(this.props, this.state).hash, this.orgStateHash, this.handleSave, this.props.onCancel, Translations.YouHaveUnapprovedChangesDoYouWantApproveChanges);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const content = <>
            {!!props.onSaveReport && !!props.onRemoveReport &&
                <FilterSearchField
                    filter={props.report}
                    canRemoveSharedFilters={props.canRemoveSharedReports}
                    isReport={true}
                    onSaveFilter={this.handleSaveReport}
                    onRemoveFilter={props.onRemoveReport}
                />
            }
            {!!props.chartPanelDrawerMode &&
                <>
                    <Box display="flex">
                        <FormControlLabel
                            label={Translations.PanelVisible}
                            control={
                                <Checkbox
                                    checked={state.panelVisible}
                                    onChange={this.handleTogglePanelVisible}
                                />
                            } />
                    </Box>
                    <GridRow>
                        <Grid item xs={6}>
                            <FormField title={Translations.PanelSize} fullWidth>
                                <IdTitleDropdown
                                    selectedId={state.panelSize}
                                    items={[
                                        { id: "1", title: Translations.PanelSize1 },
                                        { id: "2", title: Translations.PanelSize2 },
                                        { id: "5", title: Translations.PanelSize5 },
                                        { id: "3", title: Translations.PanelSize3 },
                                        { id: "4", title: Translations.PanelSize4 },
                                        { id: "6", title: Translations.PanelSize6 }
                                    ]}
                                    onChange={(panelSize) => this.setState({ panelSize })}
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={6}>
                            <FormField title={Translations.ChartType} fullWidth>
                                <IdTitleDropdown
                                    selectedId={state.selectedChartId}
                                    items={props.charts}
                                    onChange={(selectedChartId) => this.setState({ selectedChartId })}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </>
            }
            <FormField title={Translations.WhistleBlowDateAndTime}>
                <GridRow>
                    {!props.chartPanelDrawerMode &&
                        <Grid item xs={6}>
                            <DatePicker
                                value={state.startDateStr}
                                disabled={state.startEndDateDisabled}
                                onChange={this.handleStartDateChange}
                            />
                        </Grid>
                    }
                    {!props.chartPanelDrawerMode &&
                        <Grid item xs={6}>
                            <DatePicker
                                value={state.endDateStr}
                                disabled={state.startEndDateDisabled}
                                onChange={this.handleEndDateChange}
                            />
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <IdTitleDropdown
                            selectedId={state.selectedDynamicTimeFilterId}
                            items={props.chartPanelDrawerMode ? props.panelsDynamicTimeFilters : props.dynamicTimeFilters}
                            onChange={this.handleSelectedDynamicTimeFilterIdChange}
                        />
                    </Grid>
                </GridRow>
            </FormField>
            <GridRow>
                <Grid item xs={6}>
                    <FormField title={Translations.State} fullWidth>
                        <IdTitleDropdown
                            selectedId={state.selectedStateFilterId}
                            items={props.stateFilters}
                            onChange={(selectedStateFilterId) => this.setState({ selectedStateFilterId })}
                        />
                    </FormField>
                </Grid>
                <Grid item xs={6}>
                    <FormField title={Translations.Severity} fullWidth>
                        <IdTitleDropdown
                            selectedId={state.selectedSeverityFilterId}
                            items={props.severityFilters}
                            onChange={(selectedSeverityFilterId) => this.setState({ selectedSeverityFilterId })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item xs={6}>
                    <FormField title={Translations.Resolution} fullWidth>
                        <IdTitleDropdown
                            selectedId={state.selectedResolutionFilterId}
                            items={props.resolutionFilters}
                            onChange={(selectedResolutionFilterId) => this.setState({ selectedResolutionFilterId })}
                        />
                    </FormField>
                </Grid>
                <Grid item xs={6}>
                    <FormField title={Translations.Source} fullWidth>
                        <IdTitleDropdown
                            selectedId={state.selectedSourceFilterId}
                            items={props.sourceFilters}
                            onChange={(selectedSourceFilterId) => this.setState({ selectedSourceFilterId })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item xs={6}>
                    <FormField title={Translations.Category} fullWidth>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedCategoryIds}
                            toggleAll={true}
                            toggleAllId={Base.emptyGuid}
                            values={props.categories}
                            onChange={this.handleChangeCategoryIds}
                        />
                    </FormField>
                </Grid>
                <Grid item xs={6}>
                    <FormField title={Translations.Unit} fullWidth>
                        <TreeCheckboxDropdown
                            required={true}
                            selectedIds={state.selectedUnitIds}
                            values={props.units}
                            onChange={this.handleChangeUnitIds}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            <GridRow>
                <Grid item xs={6}>
                    <FormField title={Translations.EventHandler} fullWidth>
                        <CheckboxDropdown
                            required={true}
                            selectedIds={state.selectedHandlerUserCodes}
                            toggleAll={true}
                            values={props.handlers}
                            onChange={this.handleChangeHandlerUserCodes}
                        />
                    </FormField>
                </Grid>
                <Grid item xs={6}>
                    <FormField title={Translations.Anonymity} fullWidth>
                        <IdTitleDropdown
                            selectedId={state.selectedAnonymityFilterId}
                            items={props.anonymityFilters}
                            onChange={(selectedAnonymityFilterId) => this.setState({ selectedAnonymityFilterId })}
                        />
                    </FormField>
                </Grid>
            </GridRow>
            {!!state.extraParameters &&
                <Box sx={{ marginTop: "1rem" }}>
                    <ReportFilterPanelExtraParameters
                        report={props.report}
                        extraParameters={state.extraParameters}
                        onChange={(extraParameters: ITaskDataRow) => this.setState({ extraParameters })}
                    />
                </Box>
            }
        </>;

        if (props.chartPanelDrawerMode) {
            return (
                <PageChartFilterDrawer
                    title={props.report.title}
                    buttons={
                        <FilterButtons
                            chartPanelPropertiesMode
                            marginTop="0"
                            onCancel={this.handleCancel}
                            onClear={this.handleClear}
                            onRemove={undefined}
                            onSave={this.handleSave}
                        />
                    }
                    onClose={() => this.handleCancel(false)}
                >
                    {content}
                </PageChartFilterDrawer>
            );
        }

        return (
            <Box width="100%" sx={{ paddingTop: "1rem" }}>
                {content}
                <FilterButtons
                    chartPanelPropertiesMode={false}
                    onCancel={undefined}
                    onClear={this.handleClear}
                    onSave={this.handleSave}
                />
            </Box>
        );
    }
}